import React, { useEffect, useState } from 'react';
import { Container, Typography, Table, TableBody, TableCell, TableHead, TableRow, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { useAuth } from '../AuthContext';
import { API_URL } from '../config';

export default function AdminUsersPage() {
  const { token } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editNickname, setEditNickname] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editProfilePicture, setEditProfilePicture] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editError, setEditError] = useState('');
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupUser, setGroupUser] = useState(null);
  const [groupId, setGroupId] = useState('');
  const [groupAction, setGroupAction] = useState('assign');
  const [groupError, setGroupError] = useState('');
  const [groups, setGroups] = useState([]);
  useEffect(() => {
  fetch(`${API_URL}/groups`).then(res => res.ok ? res.json() : []).then(data => setGroups(data));
  }, []);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newNickname, setNewNickname] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [createError, setCreateError] = useState('');
  const fetchUsers = () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_URL}/users`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setUsers(data))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async () => {
    setCreateError('');
    try {
  const res = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail, nickname: newNickname, password: newPassword, role: newRole })
      });
      if (!res.ok) throw new Error('Failed to create user');
      setCreateOpen(false);
      setNewEmail('');
      setNewNickname('');
      setNewPassword('');
      fetchUsers();
    } catch (err) {
      setCreateError('Failed to create user');
    }
  };
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>Manage Users</Typography>
      <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setCreateOpen(true)}>
        Create User
      </Button>
      {loading ? <CircularProgress /> : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Nickname</TableCell>
              <TableCell>Role(s)</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.nickname}</TableCell>
                <TableCell>
                  {Array.isArray(user.groups) && user.groups.length > 0 ? (
                    user.groups.map(g => `${g.group_name} (${g.role})`).join(', ')
                  ) : 'No groups'}
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 100, mr: 1 }}>
                    <InputLabel id={`role-label-${user.id}`}>Role</InputLabel>
                    <Select
                      labelId={`role-label-${user.id}`}
                      value={user.groups && user.groups[0] ? user.groups[0].role : 'user'}
                      label="Role"
                      onChange={async e => {
                        const headers = token ? { Authorization: `Bearer ${token}` } : {};
                        await fetch(`${API_URL}/users/${user.id}/change-role?group_id=${user.groups[0]?.group_id || ''}&role=${e.target.value}`, { 
                          method: 'POST',
                          headers 
                        });
                        fetchUsers();
                      }}
                    >
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  </FormControl>
                  <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => {
                    setEditUser(user);
                    setEditNickname(user.nickname);
                    setEditEmail(user.email);
                    setEditProfilePicture(user.profile_picture || '');
                    setEditPassword('');
                    setEditOpen(true);
                  }}>Edit</Button>
                  <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => {
                    setGroupUser(user);
                    setGroupOpen(true);
                  }}>Groups</Button>
                  <Button size="small" variant="outlined" color="warning" sx={{ mr: 1 }} onClick={async () => {
                    // Reset password (set to 'newpassword')
                    const headers = token ? { 
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}` 
                    } : { 'Content-Type': 'application/json' };
                    await fetch(`${API_URL}/auth/users/${user.id}`, {
                      method: 'PUT',
                      headers,
                      body: JSON.stringify({ password: 'newpassword' })
                    });
                    fetchUsers();
                  }}>Reset PW</Button>
                  <Button size="small" variant="outlined" color="error" disabled={user.groups && user.groups.some(g => g.role === 'admin') && users.filter(u => u.groups && u.groups.some(g => g.role === 'admin')).length <= 1} onClick={async () => {
                    const headers = token ? { Authorization: `Bearer ${token}` } : {};
                    await fetch(`${API_URL}/users/${user.id}`, { method: 'DELETE', headers });
                    fetchUsers();
                  }}>Delete</Button>
                </TableCell>
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField label="Nickname" fullWidth margin="normal" value={editNickname} onChange={e => setEditNickname(e.target.value)} />
          <TextField label="Email" type="email" fullWidth margin="normal" value={editEmail} onChange={e => setEditEmail(e.target.value)} />
          <TextField label="Profile Picture" fullWidth margin="normal" value={editProfilePicture} onChange={e => setEditProfilePicture(e.target.value)} />
          <TextField label="Password" type="password" fullWidth margin="normal" value={editPassword} onChange={e => setEditPassword(e.target.value)} />
          {editError && <Typography color="error">{editError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            try {
              setEditError('');
              const headers = token ? { 
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}` 
              } : { 'Content-Type': 'application/json' };
              const res = await fetch(`${API_URL}/auth/users/${editUser.id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({
                  nickname: editNickname,
                  email: editEmail,
                  profile_picture: editProfilePicture,
                  password: editPassword || undefined
                })
              });
              if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to update user');
              }
              setEditOpen(false);
              setEditError('');
              fetchUsers();
            } catch (err) {
              setEditError(err.message || 'Failed to update user');
            }
          }} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={groupOpen} onClose={() => setGroupOpen(false)}>
        <DialogTitle>Manage Groups for {groupUser?.nickname}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="group-action-label">Action</InputLabel>
            <Select labelId="group-action-label" value={groupAction} label="Action" onChange={e => setGroupAction(e.target.value)}>
              <MenuItem value="assign">Assign to Group</MenuItem>
              <MenuItem value="remove">Remove from Group</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="group-id-label">Group</InputLabel>
            <Select labelId="group-id-label" value={groupId} label="Group" onChange={e => setGroupId(e.target.value)}>
              {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </Select>
          </FormControl>
          {groupError && <Typography color="error">{groupError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupOpen(false)}>Cancel</Button>
          <Button onClick={async () => {
            try {
              const headers = token ? { Authorization: `Bearer ${token}` } : {};
              if (groupAction === 'assign') {
                await fetch(`${API_URL}/users/${groupUser.id}/assign-group?group_id=${groupId}&role=user`, { 
                  method: 'POST',
                  headers 
                });
              } else {
                await fetch(`${API_URL}/users/${groupUser.id}/remove-group?group_id=${groupId}`, { 
                  method: 'POST',
                  headers 
                });
              }
              setGroupOpen(false);
              fetchUsers();
            } catch {
              setGroupError('Failed to update group membership');
            }
          }} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            required
          />
          <TextField
            label="Nickname"
            fullWidth
            margin="normal"
            value={newNickname}
            onChange={e => setNewNickname(e.target.value)}
            required
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              value={newRole}
              label="Role"
              onChange={e => setNewRole(e.target.value)}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {createError && <Typography color="error">{createError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained" color="primary">Create</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
